import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save, Trash2, UploadCloud, Video as VideoIcon, Plus } from "lucide-react";

const ZONES = [
  { k: "reviews2", l: "Modul recenzí (negativní)" },
  { k: "after_hero", l: "Pod úvodem (hero)" },
  { k: "after_about", l: "Po sekci O hře" },
  { k: "after_nightvision", l: "Po nočním vidění" },
  { k: "after_gallery", l: "Po galerii" },
  { k: "after_reviews", l: "Po recenzích" },
  { k: "before_contact", l: "Před kontaktem" },
  { k: "before_footer", l: "Před patičkou" },
];
const ORIENTATIONS = [
  { k: "vertical", l: "Vertikální (9:16)" },
  { k: "horizontal", l: "Horizontální (16:9)" },
  { k: "square", l: "Čtvercové (1:1)" },
];
const CHUNK = 4 * 1024 * 1024;
const selCls = "bg-black border border-white/15 text-neutral-200 text-sm p-2 rounded-none";

function ZoneLabel({ k }) { return <>{ZONES.find((z) => z.k === k)?.l || k}</>; }

function UploadForm({ onDone }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [orientation, setOrientation] = useState("vertical");
  const [zone, setZone] = useState("reviews2");
  const [autoplay, setAutoplay] = useState(false);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    if (!file) { toast.error("Vyber video soubor."); return; }
    setBusy(true); setProgress(0);
    try {
      const { data: init } = await api.post("/admin/videos/upload-init");
      const uploadId = init.upload_id;
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", i);
        fd.append("file", blob, `chunk-${i}`);
        // eslint-disable-next-line no-await-in-loop
        await api.post("/admin/videos/upload-chunk", fd, {
          onUploadProgress: (e) => {
            const frac = e.total ? e.loaded / e.total : 1;
            setProgress(Math.min(99, Math.round(((i + frac) / total) * 100)));
          },
        });
      }
      const { data } = await api.post("/admin/videos/upload-complete", { upload_id: uploadId, filename: file.name, total });
      await api.post("/admin/videos", { title, url: data.url, storage_path: data.path, orientation, zone, autoplay, enabled: true });
      setProgress(100);
      toast.success("Video nahráno a přidáno.");
      setFile(null); setTitle("");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nahrání selhalo.");
    } finally { setBusy(false); setTimeout(() => setProgress(0), 800); }
  };

  return (
    <div className="border border-nv/30 bg-[#0b0a09] p-5 space-y-3" data-testid="video-upload-form">
      <h3 className="font-head text-lg text-neutral-100 flex items-center gap-2"><UploadCloud className="w-4 h-4 text-nv" /> Nahrát video</h3>
      <p className="text-xs text-neutral-500">Podporované formáty: mp4, mov, webm, m4v, ogg. Max 200 MB. Nahrává se po částech (funguje i pro velká videa).</p>
      <input type="file" accept="video/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="video-file-input" className="block w-full text-sm text-neutral-400 file:mr-3 file:py-2 file:px-4 file:border file:border-white/15 file:bg-black file:text-neutral-200 file:text-sm" />
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-neutral-400">Popisek (nepovinné)</label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="video-title" className="rounded-none bg-black border-white/15 text-sm mt-1" />
        </div>
        <div>
          <label className="text-xs text-neutral-400">Umístění na webu</label>
          <select value={zone} onChange={(e) => setZone(e.target.value)} data-testid="video-zone" className={`${selCls} w-full mt-1`}>
            {ZONES.map((z) => <option key={z.k} value={z.k}>{z.l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-neutral-400">Orientace</label>
          <select value={orientation} onChange={(e) => setOrientation(e.target.value)} data-testid="video-orientation" className={`${selCls} w-full mt-1`}>
            {ORIENTATIONS.map((o) => <option key={o.k} value={o.k}>{o.l}</option>)}
          </select>
        </div>
        <label className="flex items-center justify-between gap-3 border border-white/10 px-3 py-2 bg-black mt-5" data-testid="video-autoplay">
          <span className="text-sm text-neutral-300">Automatické přehrání (ztlumeně)</span>
          <Switch checked={autoplay} onCheckedChange={setAutoplay} />
        </label>
      </div>
      {busy && (
        <div className="h-2 bg-black border border-white/10" data-testid="video-progress">
          <div className="h-full bg-nv transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}
      <button onClick={upload} disabled={busy || !file} data-testid="video-upload-btn" className="hud-btn vhs-btn flex items-center gap-2 px-4 py-2 text-sm font-head disabled:opacity-40">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Nahrávám… {progress}%</> : <><Plus className="w-4 h-4" /> Nahrát a přidat</>}
      </button>
    </div>
  );
}

function VideoRow({ v, onChange }) {
  const patch = async (body) => { try { await api.put(`/admin/videos/${v.id}`, body); onChange(); } catch (e) { toast.error("Změna selhala."); } };
  const del = async () => { if (!window.confirm("Opravdu smazat toto video z webu?")) return; try { await api.delete(`/admin/videos/${v.id}`); toast.success("Smazáno."); onChange(); } catch (e) { toast.error("Smazání selhalo."); } };
  const [title, setTitle] = useState(v.title || "");

  return (
    <div className="border border-white/10 bg-[#0b0a09] p-4 flex flex-col md:flex-row gap-4" data-testid={`video-row-${v.id}`}>
      <div className="w-full md:w-48 shrink-0">
        <video src={v.url} className="w-full max-h-40 object-contain bg-black border border-white/10" controls preload="none" />
      </div>
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => title !== v.title && patch({ title })} placeholder="Popisek" data-testid={`video-edit-title-${v.id}`} className="rounded-none bg-black border-white/15 text-sm max-w-xs" />
          {!v.enabled && <span className="text-[10px] px-2 py-0.5 border text-red-400 border-red-800/40 tracking-widest uppercase">skryto</span>}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <select value={v.zone} onChange={(e) => patch({ zone: e.target.value })} data-testid={`video-edit-zone-${v.id}`} className={selCls}>
            {ZONES.map((z) => <option key={z.k} value={z.k}>{z.l}</option>)}
          </select>
          <select value={v.orientation} onChange={(e) => patch({ orientation: e.target.value })} data-testid={`video-edit-orientation-${v.id}`} className={selCls}>
            {ORIENTATIONS.map((o) => <option key={o.k} value={o.k}>{o.l}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-4 flex-wrap pt-1">
          <label className="flex items-center gap-2 text-xs text-neutral-300"><Switch checked={!!v.enabled} onCheckedChange={(c) => patch({ enabled: c })} data-testid={`video-toggle-enabled-${v.id}`} /> Zobrazit na webu</label>
          <label className="flex items-center gap-2 text-xs text-neutral-300"><Switch checked={!!v.autoplay} onCheckedChange={(c) => patch({ autoplay: c })} data-testid={`video-toggle-autoplay-${v.id}`} /> Autoplay (ztlumeně)</label>
          <button onClick={del} data-testid={`video-delete-${v.id}`} className="ml-auto p-2 text-red-400 hover:bg-red-950/30 border border-red-900/40"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

export default function AdminVideos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.get("/admin/videos").then(({ data }) => setVideos(data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  return (
    <div className="space-y-5" data-testid="admin-videos">
      <div>
        <h2 className="font-head text-xl text-neutral-100 flex items-center gap-2"><VideoIcon className="w-5 h-5 text-nv" /> Videa na webu</h2>
        <p className="text-xs text-neutral-500 mt-1">Nahraj videa a urči, kam na web patří. Můžeš je kdykoli skrýt bez smazání.</p>
      </div>
      <UploadForm onDone={load} />
      {loading ? (
        <div className="p-4 text-neutral-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Načítám…</div>
      ) : videos.length === 0 ? (
        <p className="text-neutral-600 text-sm p-4 border border-dashed border-white/10 text-center">Zatím žádná videa.</p>
      ) : (
        <div className="space-y-3">
          {videos.map((v) => <VideoRow key={v.id} v={v} onChange={load} />)}
        </div>
      )}
    </div>
  );
}
